import React, { useState } from 'react';
import authService from '../services/authService';
import Button from './ui/Button';
import { showSuccess, showError, showWarning } from '../utils/toast'; // #204
import API_URL from '../config';
import './WorkflowButtons.css';

function WorkflowButtons({ ncId, currentStatus, onStatusChange, user, analiseVazia }) {
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [observacao, setObservacao] = useState('');

  const role = user?.role || 'operador';
  const userEmail = user?.email || 'usuario@empresa.com';

  const handleAction = (action) => {
    setModalAction(action);
    setShowModal(true);
  };

  const confirmAction = async () => {
    if (modalAction === 'reprovar' && !observacao.trim()) {
      showWarning('Motivo da reprovação é obrigatório');
      return;
    }

    setLoading(true);
    try {
      const token = authService.getToken();
      const response = await fetch(
        `${API_URL}/api/v2/nc/${ncId}/workflow/${modalAction}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ usuario: userEmail, observacao: observacao || null })
        }
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        // #281 — gate de encerramento (422): regra de negócio, não erro técnico.
        // Mostra a mensagem do backend como aviso e mantém o status.
        if (response.status === 422) {
          showWarning(err.detail || 'Não é possível encerrar ainda.');
          setShowModal(false);
          return;
        }
        throw new Error(err.detail || 'Erro ao executar ação');
      }

      const data = await response.json();
      setShowModal(false);
      setObservacao('');
      if (onStatusChange) onStatusChange(data.workflow_status);
      showSuccess(`Status atualizado: ${data.workflow_status}`);
      // #281 — segregação de função = AVISO (não bloqueia): encerrou, mas sinaliza.
      if (data.segregacao_aviso) showWarning(data.segregacao_aviso);
    } catch (error) {
      console.error('Erro:', error);
      showError('Erro ao executar ação: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = () => {
    const map = {
      rascunho:              { label: 'Rascunho',              cls: 'status-rascunho' },
      aberta:                { label: 'Aberta',                cls: 'status-revisao' },
      em_analise:            { label: 'Em Análise',            cls: 'status-revisao' },
      em_revisao:            { label: 'Em Revisão',            cls: 'status-revisao' },
      aguardando_aprovacao:  { label: 'Aguardando Aprovação',  cls: 'status-revisao' },
      aprovada:              { label: 'Aprovada',              cls: 'status-aprovado' },
      aprovado:              { label: 'Aprovado',              cls: 'status-aprovado' },
      encerrada:             { label: 'Encerrada',             cls: 'status-aprovado' },
      rejeitada:             { label: 'Rejeitada',             cls: 'status-reprovado' },
      reprovado:             { label: 'Reprovado',             cls: 'status-reprovado' },
    };
    const b = map[currentStatus] || { label: currentStatus, cls: 'status-rascunho' };
    return <span className={`status-badge ${b.cls}`}>{b.label}</span>;
  };

  const getModalLabel = () => {
    const map = { revisar: 'Enviar para Revisão', aprovar: 'Aprovar NC', reprovar: 'Reprovar NC', encerrar: 'Encerrar NC' };
    return map[modalAction] || '';
  };

  // Operador/Admin: pode enviar para revisão (rascunho → em_analise/aberta)
  const canSubmitForReview = (role === 'operador' || role === 'admin') && currentStatus === 'rascunho';

  // Admin: pode aprovar e reprovar (quando em_analise, em_revisao ou aguardando_aprovacao)
  const canApproveReject = role === 'admin' && (
    currentStatus === 'em_analise' || 
    currentStatus === 'em_revisao' || 
    currentStatus === 'aguardando_aprovacao'
  );

  // #281 — Admin pode ENCERRAR a NC quando já Aprovada (fecha o ciclo / stepper).
  // O gate "só Eficaz encerra" é do backend (flag GATE_EFICACIA_ENCERRAMENTO_HABILITADO);
  // o front só oferece o botão e trata o 422/aviso de segregação.
  const canEncerrar = role === 'admin' && (currentStatus === 'aprovada' || currentStatus === 'aprovado');

  // Status final (com a NC já Aprovada e admin podendo encerrar, mostramos a AÇÃO
  // em vez do beco "Aprovada" — o badge no topo já indica o status).
  const isFinal = ['aprovado', 'aprovada', 'reprovado', 'rejeitada', 'encerrada'].includes(currentStatus);

  return (
    <div className="workflow-buttons-container">
      <div className="workflow-header">
        <h4>Workflow Status</h4>
        {getStatusBadge()}
      </div>

      <div className="workflow-actions">
        {canSubmitForReview && (
          <button
            className="workflow-action-item"
            onClick={() => handleAction('revisar')}
            disabled={loading}
          >
            <div className="workflow-action-icon revisar">
              <span className="material-symbols-outlined">send</span>
            </div>
            <div className="workflow-action-text">
              <span className="workflow-action-title">Enviar para Revisão</span>
              <span className="workflow-action-desc">Encaminhar para o Board de Qualidade</span>
            </div>
            <span className="workflow-action-arrow material-symbols-outlined">chevron_right</span>
          </button>
        )}

        {canApproveReject && (
          <>
            {/* #216: não oferecer "Aprovar" sobre análise vazia/falha — o
                usuário aprovaria uma NC sem causa, porquês ou ações. */}
            {analiseVazia ? (
              <div className="workflow-info">
                <p>
                  <span className="material-symbols-outlined workflow-info-icon">pending</span>
                  Análise pendente — rode a análise com IA antes de aprovar
                </p>
              </div>
            ) : (
            <button
              className="workflow-action-item"
              onClick={() => handleAction('aprovar')}
              disabled={loading}
            >
              <div className="workflow-action-icon aprovar">
                <span className="material-symbols-outlined">check_circle</span>
              </div>
              <div className="workflow-action-text">
                <span className="workflow-action-title">Aprovar NC</span>
                <span className="workflow-action-desc">Confirmar análise e ações</span>
              </div>
              <span className="workflow-action-arrow material-symbols-outlined">chevron_right</span>
            </button>
            )}
            <button
              className="workflow-action-item"
              onClick={() => handleAction('reprovar')}
              disabled={loading}
            >
              <div className="workflow-action-icon reprovar">
                <span className="material-symbols-outlined">cancel</span>
              </div>
              <div className="workflow-action-text">
                <span className="workflow-action-title">Reprovar NC</span>
                <span className="workflow-action-desc">Solicitar revisão com motivo</span>
              </div>
              <span className="workflow-action-arrow material-symbols-outlined">chevron_right</span>
            </button>
          </>
        )}

        {/* #281 — Encerrar NC (admin, quando Aprovada). O gate de eficácia é do backend. */}
        {canEncerrar && (
          <button
            className="workflow-action-item"
            onClick={() => handleAction('encerrar')}
            disabled={loading}
          >
            <div className="workflow-action-icon aprovar">
              <span className="material-symbols-outlined">lock</span>
            </div>
            <div className="workflow-action-text">
              <span className="workflow-action-title">Encerrar NC</span>
              <span className="workflow-action-desc">Fechar o ciclo após a eficácia verificada</span>
            </div>
            <span className="workflow-action-arrow material-symbols-outlined">chevron_right</span>
          </button>
        )}

        {/* Operador vê NC em análise mas não pode aprovar */}
        {role === 'operador' && (currentStatus === 'em_analise' || currentStatus === 'em_revisao') && (
          <div className="workflow-info">
            <p>
              <span className="material-symbols-outlined workflow-info-icon">hourglass_empty</span>
              Aguardando aprovação do Admin
            </p>
          </div>
        )}

        {/* Auditor vê status mas não pode alterar */}
        {role === 'auditor' && !isFinal && (
          <div className="workflow-info">
            <p>
              <span className="material-symbols-outlined workflow-info-icon">visibility</span>
              Modo visualização (Auditor)
            </p>
          </div>
        )}

        {isFinal && (
          <div className="workflow-final">
            <p>
              {(currentStatus === 'aprovado' || currentStatus === 'aprovada') ? (
                <>
                  <span className="material-symbols-outlined workflow-final-icon">check_circle</span>
                  Aprovada
                </>
              ) : currentStatus === 'encerrada' ? (
                <>
                  <span className="material-symbols-outlined workflow-final-icon">lock</span>
                  Encerrada
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined workflow-final-icon">cancel</span>
                  Rejeitada
                </>
              )}
            </p>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>{getModalLabel()}</h3>
            <p className="modal-user-info">Executando como: {userEmail} ({role})</p>

            <div className="form-group">
              <label>
                {modalAction === 'reprovar' ? 'Motivo (obrigatório)' : 'Observação (opcional)'}
              </label>
              <textarea
                id="workflow-observacao"
                name="observacao"
                value={observacao}
                onChange={e => setObservacao(e.target.value)}
                rows="4"
                placeholder={
                  modalAction === 'reprovar'
                    ? 'Descreva o motivo da reprovação...'
                    : 'Adicione uma observação...'
                }
              />
            </div>

            <div className="modal-actions">
              <Button variant="secondary" onClick={() => setShowModal(false)} disabled={loading}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={confirmAction} disabled={loading}>
                {loading ? 'Processando...' : 'Confirmar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WorkflowButtons;
